import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Tooltip } from "@mui/material";
import Button from "../../components/Button/Button";
import { setCurrentStep } from "../../redux/slices/OfficeOnboardingSlice";
import { AppDispatch, RootState } from "../../redux/store";

const WelcomeStep: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  const handleContinue = () => {
    // Update localStorage to step 1
    const stored = localStorage.getItem("officeOnboardingStatus");
    if (stored) {
      try {
        const status = JSON.parse(stored);
        status.currentStep = 1;
        localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));
      } catch (e) {
        // Ignore parse errors
      }
    }
    // Navigate to Step 1
    dispatch(setCurrentStep(1));
  };

  const officeName = currentUser?.offices?.[0]?.name || "Your Office";
  const userName = currentUser?.firstName || "there";

  return (
    <div className="flex items-center justify-center py-10 px-8">
      <div className="w-full text-center">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-full bg-[#C4E538] flex items-center justify-center">
            <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {/* Welcome Message */}
        <h1 className="text-base font-inter mb-3 tracking-[-0.31px]">Welcome to Docnote, {userName}!</h1>
        <p className="text-lg font-inter tracking-[-0.41px] mb-8">Let&apos;s get your office set up so you can start seeing patients</p>

        {/* Office Name Display */}
        <div className="mb-8 bg-gray-100 rounded-lg p-6 font-inter">
          <div className="flex items-center justify-center gap-1 mb-2">
            <p className="text-[#364153] tracking-[-0.31px]">Office Name</p>
            <Tooltip title="The office name is editable post sign up from Office Info tab" placement="right" arrow>
              <HelpOutlineIcon className="!text-base text-gray-400" />
            </Tooltip>
          </div>
          <p className="text-lg font-medium tracking-[-0.44px]">{officeName}</p>
        </div>

        {/* Continue Button */}
        <div className="mb-4 flex justify-center">
          <Button
            text="Continue Office Setup"
            onClick={handleContinue}
            variant="Primary"
            width="fit"
            className="px-12 py-4 text-base rounded-full h-[56px] w-[242px]"
          />
        </div>

        {/* Time Estimate */}
        <div className="flex items-center justify-center text-sm text-[#6A7282] mb-12">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
          </svg>
          <span>5 Quick Steps</span>
        </div>

        {/* What You'll Set Up */}
        <div className="border-t border-gray-200 pt-8 font-inter">
          <h2 className="text-sm text-[#6A7282] mb-4">What you&apos;ll set up:</h2>
          <div className="grid grid-cols-2 gap-4 tracking-[-0.15px] text-sm text-left">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full border-2 border-docnote-orange flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-docnote-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-black">Office Information</span>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full border-2 border-docnote-orange flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-docnote-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-black">Custom Letterhead</span>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full border-2 border-docnote-orange flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-docnote-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-black">Primary Care Provider Profile</span>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full border-2 border-docnote-orange flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-docnote-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-black">Service Pricing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
